import React from 'react';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import HolidayVillageOutlined from '@mui/icons-material/HolidayVillageOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useEaseOutProgress } from 'hooks/use-ease-out-progress';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

export interface TypeBreakdownBarsProps {
  areaName?: string;
  totalHouses: number;
  totalTownhouses: number;
  totalCondos: number;
  totalRentals: number;
  avgHousePrice: number;
  avgTownhousePrice: number;
  avgCondoPrice: number;
  avgRentalPrice: number;
}

type TypeRow = {
  key: string;
  label: string;
  count: number;
  avgPrice: number;
  colorClass: string;
  icon: React.ReactNode;
};

/**
 * Property-type bars, plus a summary sentence ("N homes for sale, averaging
 * $X") matching the design — the sale-side average is weighted by count and
 * excludes rentals, same as the design's totalActive/avgPriceLabel calc.
 * Each row also gets a plain-language sentence under its header.
 */
const TypeBreakdownBars = ({
  areaName,
  totalHouses, totalTownhouses, totalCondos, totalRentals,
  avgHousePrice, avgTownhousePrice, avgCondoPrice, avgRentalPrice,
}: TypeBreakdownBarsProps) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency, formatNumber } = useI18nFormatting();

  const rows: TypeRow[] = [
    { key: 'houses', label: t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_HOUSES, { defaultValue: 'Houses' }), count: totalHouses, avgPrice: avgHousePrice, colorClass: styles.houseBar, icon: <HomeOutlined /> },
    { key: 'townhouses', label: t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_TOWNHOUSES, { defaultValue: 'Townhouses' }), count: totalTownhouses, avgPrice: avgTownhousePrice, colorClass: styles.townhouseBar, icon: <HolidayVillageOutlined /> },
    { key: 'condos', label: t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_CONDOS, { defaultValue: 'Condos' }), count: totalCondos, avgPrice: avgCondoPrice, colorClass: styles.condoBar, icon: <ApartmentOutlined /> },
    { key: 'rentals', label: t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_RENTALS, { defaultValue: 'Rentals' }), count: totalRentals, avgPrice: avgRentalPrice, colorClass: styles.rentalBar, icon: <KeyOutlined /> },
  ].filter(row => row.count > 0);

  // Bars fill in from 0 on mount, same ease-out curve as the gauge.
  const progress = useEaseOutProgress([totalHouses, totalTownhouses, totalCondos, totalRentals]);

  if (rows.length === 0) {
    return null;
  }

  const maxCount = Math.max(...rows.map(row => row.count));
  const area = areaName?.trim();

  const saleRows = rows.filter(row => row.key !== 'rentals');
  const totalForSale = saleRows.reduce((sum, row) => sum + row.count, 0);
  const weightedPriceSum = saleRows.reduce((sum, row) => sum + row.count * row.avgPrice, 0);
  const avgSalePrice = totalForSale > 0 ? weightedPriceSum / totalForSale : 0;

  return (
    <div className={styles.component}>
      {totalForSale > 0 && avgSalePrice > 0 && (
        <p className={styles.summary}>
          <strong>{formatNumber(totalForSale)}</strong>{' '}
          {t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_SUMMARY, { avgPrice: formatCurrency(avgSalePrice), defaultValue: 'homes for sale, averaging {{avgPrice}}.' })}
        </p>
      )}
      <div className={styles.rows}>
        {rows.map(row => (
          <div key={row.key} className={styles.row}>
            <div className={buildClassName(styles.icon, row.colorClass)}>{row.icon}</div>
            <div className={styles.rowContent}>
              <div className={styles.rowHeader}>
                <span className={styles.label}>{row.label}</span>
                <span className={styles.meta}>
                  {t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_ACTIVECOUNT, { count: row.count, defaultValue: '{{count}} active' })}
                  {row.avgPrice > 0 && <> · {formatCurrency(row.avgPrice)}</>}
                </span>
              </div>
              {area && row.avgPrice > 0 && (
                <p className={styles.subtext}>
                  {row.key === 'rentals'
                    ? t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_RENTSUBTEXT, {
                      count: row.count,
                      type: row.label.toLowerCase(),
                      area,
                      price: formatCurrency(row.avgPrice),
                      defaultValue: 'There are {{count}} {{type}} for rent in {{area}}, at a median price of {{price}}.',
                    })
                    : t(LOCALE_COMMON_PATHS.MARKETFAQ_TYPEBREAKDOWN_SALESUBTEXT, {
                      count: row.count,
                      type: row.label.toLowerCase(),
                      area,
                      price: formatCurrency(row.avgPrice),
                      defaultValue: 'There are {{count}} {{type}} for sale in {{area}}, at a median price of {{price}}.',
                    })}
                </p>
              )}
              <div className={styles.track}>
                <div className={buildClassName(styles.fill, row.colorClass)} style={{ width: `${(row.count / maxCount) * 100 * progress}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TypeBreakdownBars;
